<template>
  <div class="col-sm-12 nopads">
    <div class="col-sm-12 editor-form">
        <h2 class="editor-header">{{ modelUpdate.id > 0 ?  $t('pricing_model_conversion.edit_mode') : $t('pricing_model_conversion.add_mode') }}</h2>
      <div class="col-sm-12 form-data nopads">
        <span class="span-title">{{ $t('pricing_model_conversion.code') }}</span>
        <multiselect
          v-model="code"
          :options="forestCodeOptions"
          :multiple="false"
          :close-on-select="true"
          label="text"
          maxHeight="180"
          track-by="value"
          :searchable="true"
          :selected-label="$t('common.selected')"
          :select-label="$t('common.select')"
          :deselect-label="$t('common.remove_selection')"
          :placeholder="$t('pricing_model_conversion.select_code')"
          :disabled="loading"
        >
          <template
            #singleLabel="props"
          >
            <span class="option__desc">
              <span class="option__title">{{ props.option.text }}</span>
            </span>
          </template>
          <template
            #option="props"
          >
            <div class="option__desc">
              <span class="option__title multiselect_detail_block">{{ props.option.text }}</span>
            </div>
          </template>
          <template #noResult>
            <span>{{ $t('common.not_found') }}</span>
          </template>
          <template #noOptions>
            <span>{{ $t('common.no_results') }}</span>
          </template>
        </multiselect>
        <span class="span-title">{{ $t('tasktypes.conversion.task_type') }}</span>
        <multiselect
          v-model="taskType"
          :options="taskTypeOptions"
          :multiple="false"
          :close-on-select="true"
          label="text"
          track-by="value"
          maxHeight="160"
          :searchable="true"
          :selected-label="$t('common.selected')"
          :select-label="$t('common.select')"
          :deselect-label="$t('common.remove_selection')"
          :placeholder="$t('tasktypes.conversion.select_task_type')"
          :disabled="loading"
        >
          <template
            #singleLabel="props"
          >
            <span class="option__desc">
              <span class="option__title">{{ props.option.text }}</span>
            </span>
          </template>
          <template
            #option="props"
          >
            <div class="option__desc">
              <span class="option__title multiselect_detail_block">{{ props.option.text }}</span>
            </div>
          </template>
          <template #noResult>
            <span>{{ $t('common.not_found') }}</span>
          </template>
          <template #noOptions>
            <span>{{ $t('common.no_results') }}</span>
          </template>
        </multiselect>

          <div class="col-sm-12 text-end my-2" style="padding-top: 6em">
          <button class="btn btn-secondary form-button" @click.stop="$emit('close')">
            {{ $t('common.cancel') }}
          </button>
          <button class="btn btn-success form-button" :disabled="loading || !code || !taskType || !code.value || !taskType.value" @click.stop="submit">
             {{ $t('common.save') }}
          </button>
        </div>
      </div>
    </div>
    <div
      v-if="loading"
      id="loader"
      class="spinner"
    />
    <base-modal v-if="deletion" @ok="deleteConversionModel()" @close="deletion = false">
      <div class="modal-body">
        <span>{{ $t('common.confirm_delete') }}</span>
      </div>
    </base-modal>
  </div>
</template>

<script>
import {conversionModelHelper} from '@/components/mixins/ConversionMixin'
import {restApi} from '@/components/mixins/RestApiMixin'
import BaseModal from "@/components/BaseModal";
import {inject} from "vue";

export default {
    name: 'ConversionModelEditor',
    components: {BaseModal},
    mixins: [conversionModelHelper, restApi],
    props: {
        model: {
            type: Object,
            default: null
        },
        conversionModels: {
            type: Array,
            default() {
                return []
            }
        },
        codes: {
            type: Array,
            default() {
                return []
            }
        }
    },
    setup() {
        // Inject parents alerter
        const showAlert = inject('showAlert');
        return { showAlert };
    },
    data: function () {
        return {
            loading: false,
            code: null,
            deletion: false,
            taskType: null,
            taskTypes: [],
            taskTypeOptions: [],
            modelUpdate: this.jsonToItem(this.model),
        }
    },

    computed: {
        forestCodeOptions() {
            const nullVal = { value: null, text: this.$t('tasktypes.conversion.select_code') }
            if ((!this.codes || !Object.keys(this.codes).length) || this.loading) {
                return [nullVal]
            }
            // Sort by code
            this.codes.sort((a, b) => a.code - b.code);
            return [
                nullVal,
                ...this.codes.map(item => ({
                    value: item.code,
                    id: item.id,
                    text: item.code +' - ' + item.name
                }))
            ]
        },
    },
    mounted: function () {
        this.fetchTaskTypes()
        if (this.modelUpdate.id > 0) {
            if (this.modelUpdate.code && this.modelUpdate.code.id) {
                let foundCode = this.codes.find(item => item.id === this.modelUpdate.code.id)
                if (foundCode) {
                    this.code = {text: foundCode.name + ' ' + '(' + foundCode.code + ')', value: foundCode.code, id: foundCode.id}
                }
            }
        }
    },

    methods: {
        fetchTaskTypes: function () {
            this.loading = true
            this.restFetch(this.taskTypeUrl, this.handleTaskTypesSuccess)
        },

        handleTaskTypesSuccess: function (response) {
            this.taskTypes = response.data
            this.handleTaskTypes()
            this.loading = false
        },

        handleTaskTypes() {
            this.taskTypeOptions = []
            // set empty option value
            this.taskTypeOptions.push({text: this.$t('tasktypes.conversion.select_task_type'), value: null})
            this.taskTypes && this.taskTypes.forEach(taskType => {
                if(this.modelUpdate.task_type && this.modelUpdate.task_type.id && taskType.id === this.modelUpdate.task_type.id) {
                    // set preselected pricing model
                    this.taskType = {text: taskType.name, value: taskType.id}
                }
                this.taskTypeOptions.push({text: taskType.name, value: taskType.id})
            })
        },

        setDefaultValues: function (model) {
            if (!this.code || !this.code.value) {
                this.showAlert(this.$t('tasktypes.conversion.invalid_code'))
                return undefined
            }
            if (!this.taskType || !this.taskType.value) {
                this.showAlert(this.$t('tasktypes.conversion.invalid_task_type'))
                return undefined
            }
            if(this.code && this.code.id) {
                model.code = this.codes.find(item => item.id === this.code.id)
            }
            if(this.taskType) {
                model.task_type = this.taskTypes.find(item => item.id === this.taskType.value)
            }
            if(this.checkForDuplicates(model)) {
                this.showAlert(this.$t('tasktypes.conversion.duplicate_conversion'))
                return undefined
            }
            return model
        },

        submit: function () {
            let json = this.setDefaultValues(this.modelUpdate)
            if (json !== undefined) {
                this.loading = true
                if (this.modelUpdate.id < 1) {
                    this.restAdd(this.taskTypeConversionUrl, json, this.success, this.fail)
                } else {
                    this.restUpdate(this.taskTypeConversionUrl, json, this.success, this.fail)
                }
            }
        },

        checkForDuplicates(updateModel) {
            if(this.conversionModels !== null) {
                const exist = this.conversionModels.find(cModel =>
                    cModel.hasOwnProperty('code') &&
                    updateModel.code && cModel.code.id === updateModel.code.id &&
                    cModel.id !== updateModel.id
                )
                if (exist) {
                    return true
                }
            }
            return false
        },

        deleteConversionModel: function () {
            this.deletion = false
            this.loading = true
            var url = this.taskTypeConversionUrl + '/' + this.modelUpdate.id
            this.restDelete(url, this.success, this.fail)
        },

        confirmDeleteConversionModel() {
            if (!this.loading) {
                this.deletion = true
            }
        },

        success: function () {
            this.$emit('closeAndUpdate')
            this.loading = false
        },

        fail: function () {
            this.showAlert(this.$t('common.alert_update_failed'))
            this.loading = false
        }

    }
}
</script>
